import React from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

export interface ThemeChip {
    key: string
    label: string
    count: number
    selected: boolean
    icon: React.ReactNode
}

interface PackageThemeChipsProps {
    chips: ThemeChip[]
    onToggle: (key: string) => void
    /** Names the row for screen readers, which cannot see it sits above the grid. */
    groupLabel: string
    isMobile: boolean
}

const PackageThemeChips: React.FC<PackageThemeChipsProps> = ({ chips, onToggle, groupLabel, isMobile }) => {
    if (!chips.length) return null

    return (
        <Box
            role="group"
            aria-label={groupLabel}
            sx={{
                display: 'flex',
                gap: 1.25,
                // The row scrolls rather than wrapping: on a phone it is a rail, and on a desktop
                // the chips stay on one line instead of pushing the grid down.
                overflowX: 'auto',
                pb: 0.5,
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' }
            }}
        >
            {chips.map(chip => (
                <Stack
                    key={chip.key}
                    component="button"
                    type="button"
                    direction="row"
                    spacing={1}
                    onClick={() => onToggle(chip.key)}
                    aria-pressed={chip.selected}
                    sx={{
                        flexShrink: 0,
                        alignItems: 'center',
                        px: isMobile ? 1.75 : 2,
                        py: isMobile ? 1 : 1.125,
                        borderRadius: 50,
                        border: '1px solid',
                        borderColor: chip.selected ? 'primary.main' : 'borders.default',
                        bgcolor: chip.selected ? 'primary.100' : 'background.paper',
                        color: 'textColors.primary',
                        cursor: 'pointer',
                        font: 'inherit',
                        whiteSpace: 'nowrap',
                        '& svg': { fontSize: 18, color: chip.selected ? 'primary.main' : 'textColors.subheading' },
                        '&:hover': { borderColor: 'primary.main' }
                    }}
                >
                    {chip.icon}
                    <Typography
                        sx={{
                            fontSize: isMobile ? '0.875rem' : '0.938rem',
                            fontWeight: chip.selected ? 700 : 500
                        }}
                    >
                        {chip.label}
                    </Typography>
                </Stack>
            ))}
        </Box>
    )
}

export { PackageThemeChips }
export type { PackageThemeChipsProps }
export default PackageThemeChips
